import { useCallback, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCreateGiveaway, useMe } from '@/hooks/queries'
import { clearDraftGiveaway, saveDraftGiveaway } from '@/lib/draft-storage'
import { giveawayPath, slugError, slugify } from '@/lib/slug'
import { notifyError } from '@/lib/notify'
import { cn } from '@/lib/cn'
import { Giveaway } from '@/models/giveaway'
import type { GameInfo } from '@/models/game-info'
import { Avatar } from '../ui/Avatar'
import { BasicsStep } from './BasicsStep'
import { CustomGameStep } from './CustomGameStep'
import { DetailsStep } from './DetailsStep'
import { KeysStep } from './KeysStep'
import { ObjectivesStep } from './ObjectivesStep'
import { ReviewStep } from './ReviewStep'
import {
  FIRST_STEP,
  nextStep,
  previousStep,
  railStep,
  STEP_LABELS,
  STEP_ORDER,
  type WizardStep,
} from './types'

interface GiveawayNewProps {
  /** Draft restored after the Discord bot invite round trip. */
  initialGiveaway?: Giveaway | null
  onClose: () => void
}

/**
 * Port of components/GiveawayNew.vue — the four-step create flow plus the two
 * detours (paste keys, add a custom game).
 *
 * Title editing used a `contenteditable` span driven by `document.getElementById
 * ('giveaway-title').focus()`; it is a plain input here, which is what
 * screen readers and mobile keyboards expect.
 */
export function GiveawayNew({ initialGiveaway, onClose }: GiveawayNewProps) {
  const navigate = useNavigate()
  const { data: me } = useMe()
  const create = useCreateGiveaway()

  const [giveaway, setGiveaway] = useState(
    () => initialGiveaway ?? new Giveaway({ prize_type_id: 1 }),
  )
  const [step, setStep] = useState<WizardStep>(FIRST_STEP)
  const [editingTitle, setEditingTitle] = useState(false)
  const titleInput = useRef<HTMLInputElement>(null)

  /**
   * Every step mutates a copy. The Vue version mutated the shared object
   * directly and then called `$forceUpdate()` / `$set` to make Vue notice,
   * which is exactly the reactivity gap React does not have.
   */
  const update = useCallback((mutate: (draft: Giveaway) => void) => {
    setGiveaway((current) => {
      const draft = current.clone()
      mutate(draft)
      return draft
    })
  }, [])

  const persistDraft = useCallback(() => saveDraftGiveaway(giveaway), [giveaway])

  const complete = async () => {
    if (!giveaway.name) {
      notifyError('Please give your giveaway a name.')
      return
    }

    // Fall back to the name if the organiser never opened the link field.
    const slug = giveaway.slug ?? slugify(giveaway.name)
    const problem = slugError(slug)
    if (problem) {
      notifyError(problem)
      return
    }

    const toCreate = giveaway.clone()
    toCreate.slug = slug

    const created = await create.mutateAsync(toCreate)
    clearDraftGiveaway()
    onClose()
    navigate(giveawayPath(created.get_slug || slug))
  }

  const startEditingTitle = () => {
    setEditingTitle(true)
    // The input mounts in the same commit; focus once it exists.
    window.setTimeout(() => titleInput.current?.select(), 0)
  }

  return (
    <div className="overlay-wrapper">
      <div className="content">
        <div className="heading-row">
          <Avatar
            src={me?.display_profile_picture.src}
            className="heading-avatar"
            label="Your avatar"
          />
          <div className="heading">
            <div className="title">
              {editingTitle ? (
                <input
                  ref={titleInput}
                  className="giveaway-title-input"
                  aria-label="Giveaway name"
                  value={giveaway.name ?? ''}
                  autoFocus
                  onChange={(event) =>
                    update((draft) => {
                      draft.name = event.target.value
                    })
                  }
                  onBlur={() => setEditingTitle(false)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === 'Escape') {
                      event.preventDefault()
                      setEditingTitle(false)
                    }
                  }}
                />
              ) : (
                <span
                  style={{ color: giveaway.name ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.1)' }}
                >
                  {giveaway.name || 'Enter Giveaway Name'}
                </span>
              )}
              <button
                type="button"
                className="edit-btn"
                aria-label="Edit giveaway name"
                onClick={startEditingTitle}
              >
                <img src="/icons/edit.png" alt="" />
              </button>
            </div>
            <div className="subtitle">
              event by <span className="username">&nbsp;{me?.display_name}</span>
            </div>
          </div>
        </div>

        <WizardRail step={step} />

        {step === 'basics' ? (
          <BasicsStep
            giveaway={giveaway}
            update={update}
            onBack={onClose}
            onNext={() => setStep(nextStep('basics'))}
            onCancel={onClose}
            onEditKeys={() => setStep('keys')}
            onAddCustomGame={() => setStep('custom-game')}
          />
        ) : null}

        {step === 'details' ? (
          <DetailsStep
            giveaway={giveaway}
            update={update}
            onBack={() => setStep(previousStep('details'))}
            onNext={() => setStep(nextStep('details'))}
          />
        ) : null}

        {step === 'objectives' ? (
          <ObjectivesStep
            giveaway={giveaway}
            update={update}
            onBack={() => setStep(previousStep('objectives'))}
            onNext={() => setStep(nextStep('objectives'))}
            onBeforeLeave={persistDraft}
          />
        ) : null}

        {step === 'review' ? (
          <ReviewStep
            giveaway={giveaway}
            onBack={() => setStep(previousStep('review'))}
            onComplete={complete}
            isSubmitting={create.isPending}
          />
        ) : null}

        {step === 'keys' ? (
          <KeysStep
            keys={giveaway.prizes ?? []}
            onBack={() => setStep('basics')}
            onSave={(keys) => {
              update((draft) => {
                draft.prizes = keys
              })
              setStep('basics')
            }}
          />
        ) : null}

        {step === 'custom-game' ? (
          <CustomGameStep
            initial={giveaway.game_info}
            onCancel={() => setStep('basics')}
            onConfirm={(gameInfo: GameInfo) => {
              update((draft) => {
                draft.game_info = gameInfo
                draft.game_info_id = gameInfo.id
              })
              setStep('basics')
            }}
          />
        ) : null}
      </div>
    </div>
  )
}

/**
 * A four-step progress rail. The Quasar wizard gave no indication of where you
 * were in the flow beyond the changing form, so "Next" felt open-ended.
 */
function WizardRail({ step }: { step: WizardStep }) {
  const current = railStep(step)
  const currentIndex = STEP_ORDER.indexOf(current)

  return (
    <div className="wizard-steps" role="list" aria-label="Giveaway steps">
      {STEP_ORDER.map((entry, index) => (
        <div
          key={entry}
          role="listitem"
          aria-current={entry === current ? 'step' : undefined}
          className={cn(
            'wizard-step',
            index < currentIndex && 'is-done',
            entry === current && 'is-current',
          )}
        >
          <div className="wizard-step-bar" />
          <div className="wizard-step-name">{STEP_LABELS[entry]}</div>
        </div>
      ))}
    </div>
  )
}
